import { useSnackbar } from "notistack";
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import {
    Card, CardContent, CardHeader, Grid, Chip, Box, Button
} from "@mui/material";
import { FormProvider, RHFFileField } from "../../common/hook-form";
import { LoadingButton } from "@mui/lab";
import DownloadSampleFile from "../../components/DownloadSampleFile";
import { apiConstants, post, get, postMultipart } from "../../common/constants";
import UploadIcon from '@mui/icons-material/Upload';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import * as XLSX from 'xlsx/xlsx.mjs';
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from 'yup';
import PageTitle from "../../components/PageTitle";
import MuiTabs from "../../components/MuiTabs";

const tabs = [
    { label: 'EDD Upload', value: 0 },
    { label: 'Revised EDD Upload (Delayed Shipment)', value: 1 },
];

function EddUploadTab() {
    const { enqueueSnackbar } = useSnackbar();
    const sampleName = 'EDD_UPDATE_SAMPLE';
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [data, setData] = useState([]);
    let arr = [];

    const methods = useForm({
            resolver: yupResolver(Yup.object().shape({
                file: Yup.mixed().required("Please Select a File"),
            })),
            defaultValues: {
                file:""
            }
        }
    )

    const { handleSubmit, reset } = methods

    const onSubmit = async (data) => {
        setIsSubmitting(true)
        var file = data.file[0];
        if (!file) return;
        try {

            var reader = new FileReader();
            reader.readAsBinaryString(file);

            reader.onload = async function (e) {
                setIsSubmitting(true)
                var data = e.target.result;
                var workbook = XLSX.read(data, {
                    type: 'binary',
                    cellDates: true,

                });
                var result = {};
                workbook.SheetNames.forEach(function (sheetName) {
                    var roa = XLSX.utils.sheet_to_row_object_array(workbook.Sheets[sheetName], { header: 0, raw: false, dateNF: 'yyyy-mm-dd' });
                    if (roa.length > 0) {
                        result[sheetName] = roa;

                    }
                });

                let sheetResult = result.Sheet1
                for (let i = 0; i < sheetResult?.length; i++) {
                    await post(apiConstants.EDD_UPDATE, sheetResult[i]).then(async (res) => {
                        let result = await res.data
                        arr.push(result)
                        if (res?.status === 200) {
                            enqueueSnackbar("EDD is Successfully updated", { variant: 'success' })
                        }
                        else {
                            enqueueSnackbar(result.responseObj, { variant: 'error' })
                        }
                        reset({
                            file:null
                        })

                    }).catch((err) =>
                        // (
                        //  console.log(err)
                        enqueueSnackbar(err.response?.data?.message, { variant: 'error' })
                        // )

                    )
                    setIsSubmitting(false)
                }
                setData(arr)
            }


        } catch (e) {
            console.error(e);
        }

    }

    console.log(data)
    return (
        <Card>
            <CardHeader title='Upload EDD'/>
            <CardContent>
                    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <RHFFileField name='file' />
                            </Grid>

                            <Grid item xs={12}>
                                <LoadingButton
                                    loading={isSubmitting}
                                    variant="contained"
                                    size="medium"
                                    startIcon={<UploadIcon />}
                                    type="submit"
                                >
                                    Upload
                                </LoadingButton>
                                <DownloadSampleFile fileName={sampleName} excel={"excel"} />
                            </Grid>
                        </Grid>
                    </FormProvider>
            </CardContent>
        </Card>
    );
}

function parseRowMessage(raw) {
    try {
        return JSON.parse(raw).message || raw;
    } catch (e) {
        return raw;
    }
}

function downloadFailureCsv(results, taskId) {
    const headers = ['Row Number', 'AWB Number', 'Status', 'Failure Reason'];
    const rows = (results || []).map(r => [
        r.rowNumber,
        r.awbNumber || '',
        r.status,
        parseRowMessage(r.message || '')
    ]);
    const csv = [headers, ...rows]
        .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
        .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `failed_rows_${taskId}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

function CustomEddUploadTab() {
    const { enqueueSnackbar } = useSnackbar();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [reasons, setReasons] = useState([]);
    const [uploadResult, setUploadResult] = useState(null);

    useEffect(() => {
        get(apiConstants.CUSTOM_EDD_REASONS)
            .then((res) => {
                if (res.data?.responseObj) setReasons(res.data.responseObj);
            })
            .catch((err) => console.error('Failed to load EDD reasons', err));
    }, []);

    const methods = useForm({
        resolver: yupResolver(Yup.object().shape({
            file: Yup.mixed().required("Please Select a File"),
        })),
        defaultValues: { file: null }
    });
    const { handleSubmit, reset } = methods;

    const onSubmit = async (data) => {
        setIsSubmitting(true);
        setUploadResult(null);
        try {
            const formData = new FormData();
            formData.append('file', data.file[0]);
            await postMultipart(apiConstants.CUSTOM_EDD_BULK_UPDATE, formData)
                .then((res) => {
                    if (res.status === 200) {
                        const result = res.data?.responseObj;
                        setUploadResult(result);
                        reset({ file: null });
                        if (result?.failureCount === 0) {
                            enqueueSnackbar("All rows updated successfully!", { variant: 'success' });
                        } else if (result?.successCount === 0) {
                            enqueueSnackbar(`Upload complete — all ${result.failureCount} rows failed.`, { variant: 'error' });
                        } else {
                            enqueueSnackbar(`Upload complete — ${result.successCount} succeeded, ${result.failureCount} failed.`, { variant: 'warning' });
                        }
                    } else {
                        enqueueSnackbar(res.data?.exception || 'Upload failed', { variant: 'error' });
                    }
                });
        } catch (err) {
            const message = err.response?.data?.exception || err.response?.data?.message || err.message;
            enqueueSnackbar(message, { variant: 'error' });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <RHFFileField name='file' />
                        </Grid>
                        <Grid item xs={12} sx={{ display: 'flex', gap: 1 }}>
                            <LoadingButton
                                loading={isSubmitting}
                                variant="contained"
                                size="medium"
                                startIcon={<UploadIcon />}
                                type="submit"
                            >
                                Upload
                            </LoadingButton>
                            <DownloadSampleFile fileName='BULK_CUSTOM_EDD_UPDATE' excel="excel" />
                        </Grid>
                    </Grid>
                </FormProvider>
            </Grid>

            {uploadResult && (
                <Grid item xs={12}>
                    <Card variant="outlined">
                        <CardHeader
                            title="Upload Result"
                            subheader={`Task ID: ${uploadResult.taskId}`}
                            titleTypographyProps={{ variant: 'subtitle1' }}
                            sx={uploadResult.failureCount === 0 ? { pb: 2 } : {}}
                            action={
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', pr: 1, pt: 1 }}>
                                    <Chip label={`Total: ${uploadResult.totalRows}`} size="small" />
                                    <Chip label={`Success: ${uploadResult.successCount}`} size="small" color="success" />
                                    <Chip label={`Failed: ${uploadResult.failureCount}`} size="small" color={uploadResult.failureCount > 0 ? 'error' : 'default'} />
                                </Box>
                            }
                        />
                        {uploadResult.failureCount > 0 && (
                            <CardContent sx={{ pt: 0 }}>
                                <Button
                                    variant="outlined"
                                    color="error"
                                    size="small"
                                    startIcon={<FileDownloadIcon />}
                                    onClick={() => downloadFailureCsv(uploadResult.results, uploadResult.taskId)}
                                >
                                    Download Failed Rows ({uploadResult.failureCount})
                                </Button>
                            </CardContent>
                        )}
                    </Card>
                </Grid>
            )}

            {reasons.length > 0 && (
                <Grid item xs={12}>
                    <Card variant="outlined">
                        <CardHeader
                            title="Valid Reasons"
                            subheader='Each row in your Excel must have exactly one of these values in the "reason" column'
                            titleTypographyProps={{ variant: 'subtitle1' }}
                        />
                        <CardContent>
                            <Grid container spacing={1}>
                                {reasons.map((reason, idx) => (
                                    <Grid item key={idx}>
                                        <Chip label={reason} variant="outlined" color="primary" size="small" />
                                    </Grid>
                                ))}
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
            )}
        </Grid>
    );
}

export default function EddUploads() {
    const [active, setActive] = useState(0);

    return (
        <>
            <PageTitle>EDD Bulk Upload</PageTitle>
            <MuiTabs tabs={tabs} active={active} setActive={setActive} />
            <Box sx={{ width: 1, boxShadow: 1, backgroundColor: '#fff', p: 2 }}>
                {active === 0 && <EddUploadTab />}
                {active === 1 && <CustomEddUploadTab />}
            </Box>
        </>
    );
}
